<template>
    <DefaultLayout
        :id="id"
        class="PhoneInput"
        input-container-class="PhoneInput__inputContainer"
        :class="{
            'PhoneInput--error': error,
            'PhoneInput--disabled': disabled,
        }"
        :label="label"
        :required="required"
        :error="error"
    >
        <template #default>
            <PhoneNumberInput
                :id="id"
                :value="value"
                :disabled="disabled"
                @input="onPhoneInput"
            />
        </template>
        <template
            v-for="name in DEFAULT_LAYOUT_SLOT_NAMES"
            #[name]
        >
            <slot :name="name" />
        </template>
    </DefaultLayout>
</template>

<style lang="scss">
@import "../style-variables.scss";

.PhoneInput {
    &__inputContainer {
        @include input-container;
    }

    // Style the intl-tel-input container to fill the input area
    .iti {
        flex: 1;
        min-width: 0;
    }

    // Style the inner input to match TextInput
    .iti input {
        @include input-field;
        width: 100%;
    }

    &--error {
        @include input-error-state(".PhoneInput__inputContainer");
    }

    &--disabled {
        @include input-disabled-state(".PhoneInput__inputContainer");
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import PhoneNumberInput from '@evidentid/dashboard-commons/components/PhoneNumberInput/PhoneNumberInput.vue';
    import { PhoneNumber } from '@evidentid/dashboard-commons/components/PhoneNumberInput/types';

    import { commonInputProps } from '@/modules/dynamic-form/components/style-inputs/common-input-props';
    import DefaultLayout from '@/modules/dynamic-form/components/style-inputs/default-layout/DefaultLayout.vue';
    import { defaultLayoutSlotForwardingMixin } from '@/modules/dynamic-form/components/style-inputs/default-layout-slot-forwarding.mixin';

    export default defineComponent({
        name: 'PhoneInput',
        components: { DefaultLayout, PhoneNumberInput },
        mixins: [ defaultLayoutSlotForwardingMixin ],
        props: {
            ...commonInputProps,
            value: {
                type: String as PropType<string>,
                default: '',
            },
        },
        emits: [ 'change', 'validityChange' ],
        methods: {
            onPhoneInput(phone: PhoneNumber): void {
                const normalized = (
                    phone.number.startsWith(`+${phone.dialCode}`)
                        ? phone.number
                        : phone.number ? `+${phone.dialCode}${phone.number}` : ''
                ).replace(/[^\d+]+/g, '');

                const isValid = !normalized || phone.valid;
                this.$emit('change', normalized);
                this.$emit('validityChange', isValid);
            },
        },
    });
</script>
